import { type Dictionary, type Lang, normalise } from '@/lib/i18n';

/**
 * The Lithuanian, as part of the site rather than as a service call.
 *
 * Every string on every page is translated in `dictionaries/lt.json`, committed
 * and shipped in the bundle. There is no translation API, no key to configure,
 * no quota to run out of and nothing to go wrong on a Sunday: the words are
 * already here by the time the page loads.
 *
 * That is a deliberate trade. A machine translator covers new copy the moment
 * it appears, which is genuinely useful — but it costs an account, a key, a
 * rate limit and a dependency that can retire a model out from under you. For a
 * site whose text changes a few times a month, translating it once and keeping
 * the answer in the repository is both better and simpler. What it asks in
 * return is that new copy gets translated too, which is written down in
 * CONTRIBUTING.md and checked by `npm run check:translations`.
 *
 * The dictionary is keyed by the English text itself, not by a code like
 * `hero.title`. Edit a sentence and it simply becomes a string the dictionary
 * does not have — which shows up in the check, rather than silently serving the
 * translation of wording that no longer exists.
 */

/** Loaded on demand: the English site never pays for the Lithuanian. */
const LOADERS: Partial<Record<Lang, () => Promise<{ default: Dictionary }>>> = {
  lt: () => import('./dictionaries/lt.json') as Promise<{ default: Dictionary }>,
};

const loaded = new Map<Lang, Dictionary>();

/**
 * The bundled translation for a language.
 *
 * Kept once per language for the life of the page. The import is a local chunk
 * — no network request to anywhere else, and nothing that can fail in a way the
 * visitor would notice: a language with no dictionary simply reads English.
 */
export async function loadDictionary(lang: Lang): Promise<Dictionary> {
  const cached = loaded.get(lang);
  if (cached) return cached;

  const loader = LOADERS[lang];
  if (!loader) return {};

  try {
    const imported = await loader();
    // Normalised on the way in so a lookup by rendered text always matches,
    // whatever whitespace JSX left behind.
    const dictionary: Dictionary = {};
    for (const [source, target] of Object.entries(imported.default)) {
      const key = normalise(source);
      if (key && typeof target === 'string' && target) dictionary[key] = target;
    }
    loaded.set(lang, dictionary);
    return dictionary;
  } catch {
    return {};
  }
}

/** Whether a language has a bundled translation at all. */
export function hasDictionary(lang: Lang): boolean {
  return Boolean(LOADERS[lang]);
}
